import { z } from 'zod';
import type { FilterSelection } from '@/features/listing-filters/types';

export const RESULT_STATUSES = ['Pending', 'Deduplicated', 'Duplicate', 'Skipped'] as const;

/**
 * Filters for one execution's results table, held in the URL so a filtered view
 * is a shareable link and Back works.
 *
 * Deliberately NOT persisted to local storage, unlike the listing pages. These
 * filters belong to one execution; restoring them onto the next execution you
 * open would hide rows for a reason that happened on a different page.
 *
 * Malformed values fall back to undefined via `.catch()`, so a hand-edited URL
 * degrades to an unfiltered table instead of a blank page.
 */
// Not exported: the only consumers are the search schema below and the type
// derived from it. The split exists so the filter component's type has no `page`
// on it, not because anything outside this file needs the filters alone — unlike
// the listing pages, this table deliberately has no stored-filter rehydrate.
const executionResultsFiltersSchema = z.object({
  entityId: z.string().min(1).max(100).optional().catch(undefined),
  status: z.array(z.enum(RESULT_STATUSES)).optional().catch(undefined),
  emitted: z
    .array(z.enum(['yes', 'no']))
    .optional()
    .catch(undefined),
  delivered: z
    .array(z.enum(['yes', 'no']))
    .optional()
    .catch(undefined),
});

export type ExecutionResultsFilterValues = z.infer<typeof executionResultsFiltersSchema>;

export const executionResultsSearchSchema = executionResultsFiltersSchema.extend({
  page: z.number().int().min(1).optional().catch(undefined),
});

/**
 * A yes/no filter group as the boolean the api takes.
 *
 * Both ticked, or neither, means no filter — which is what a pair of checkboxes
 * naturally expresses, and it keeps the group usable without a third "any"
 * option nobody would think to click.
 */
export const yesNoToBoolean = (values: string[] | undefined): boolean | undefined => {
  if (!values || values.length !== 1) return undefined;
  return values[0] === 'yes';
};

/** The filter selection the menu and pills work against. */
export const toSelection = (values: ExecutionResultsFilterValues): FilterSelection => ({
  status: values.status ?? [],
  emitted: values.emitted ?? [],
  delivered: values.delivered ?? [],
});
